import { describeClass } from './classDescription'
import {
  dayShape,
  formatSpan,
  formatTime,
  gapLabel,
  gapWord,
  type DayPart,
  type Subject,
} from './dayShape'
import { needsMarking, registerOf, type TeacherSession } from './teacher'
import type { PortalSession } from './portal'

/**
 * One person's day, as a Home row (D-380).
 *
 * **The join is the whole point.** A teacher who is also a parent has two
 * payloads that share `occurrenceId`, and only something holding both at once
 * can say *"Hailey is in this one"* or *"you finish before she does"*. Neither
 * `/schedule` view can, because each holds one half by design.
 */

/** A class inside an expanded row. */
export type RowClass = {
  occurrenceId: string
  label: string
  detail: string | null
  from: number
  to: number
  when: string
  roomName: string | null
  roomColor: string | null
  /** Who is taking it, when that is not the reader herself. */
  taughtBy: string[]
  /** She teaches this one. */
  mine: boolean
  isCancelled: boolean
  /**
   * The cross-reference — *"Hailey is in this one"*, *"same room, stays put"*.
   *
   * Only sayable because both halves are loaded, and the reason the row expands
   * rather than linking straight to the calendar.
   */
  note: string | null
  /** Still to mark. Drives whether the teaching row opens by itself. */
  needsMarking: boolean
  /**
   * The session whose register this row's *Take attendance* opens, or null.
   *
   * <b>Not the same value as `occurrenceId`</b> on a private, whose row is its
   * booking (D-733) — which is the whole reason it is a separate field rather
   * than the panel reusing the id it already has.
   */
  registerId: string | null
  /**
   * How far through the register she is, when it is hers and it has one.
   *
   * The card this replaced said "0 of 7 marked" and the row said nothing —
   * fine for a class not started and least useful on the one she left
   * half-done at the break, which is the case a count is for (D-369).
   */
  marked: { done: number; of: number } | null
}

export type TodayRow = {
  key: string
  /** "You are teaching", "You are dancing", or a dancer's first name. */
  title: string
  subject: Subject
  /**
   * The reader's own row (D-572).
   *
   * <b>Separate from <c>subject</c>, which says what kind of row it is.</b>
   * <c>tonight()</c> used to infer the reader from <c>subject === 'me'</c>,
   * which held only while a dancer row was always somebody else - the moment a
   * dancer got her own login it printed <i>"You are dancing goes on to
   * 6:45pm"</i> to the one person the row is about. A flag rather than a
   * cleverer read of the title, because a sentence built by matching words is
   * the thing that just broke.
   */
  self: boolean
  classes: RowClass[]
  parts: DayPart[]
  /** "4–6:30pm · 1 hr free · 7:30–8pm", already worded for this subject. */
  sentence: { spans: string[]; gaps: string[] }
  /**
   * The next day this person has anything, when today is not it.
   *
   * **A row with nothing today still appears**, and this is what it says. An
   * absent row cannot be told apart from a row that failed to load, which is
   * the mistake D-209 exists to prevent — and on a Wednesday Ellie dances and
   * Everly does not, in ten of the twenty-seven households with two dancers.
   *
   * It also carries the out-of-season case (D-189). The studio once saw an
   * empty screen in August and concluded attendance was broken; "nothing
   * today, next Tuesday" is the sentence that stopped that, and it belongs on
   * the row rather than on a section underneath it.
   */
  next: { date: string; classes: number; from: string | null } | null
}

const clock = (t: string | null) => (t ? Number(t.split(':')[0]) * 60 + Number(t.split(':')[1]) : 0)

/** A teacher's own session, as a row class. */
function fromTeaching(s: TeacherSession): RowClass {
  const { label, detail, who } = describeClass(s.className, s.routineName)

  /*
   * **A private is named by its dancer, and the name comes from the booking.**
   *
   * `describeClass` reads a class name, and a private has none — the server
   * sends the literal "Private lesson" — so it returned no `who` and the row
   * read *"Private lesson, 2-Yellow Room"*. Reported from testing: a teacher
   * could see she had one at six and not who it was with, which is the entire
   * content of a private lesson.
   *
   * The names ride on the booking rather than the class name, so they are taken
   * from there rather than parsed back out of a string (D-88 is the standing
   * warning about inferring facts from a name).
   */
  const named = s.isPrivate && s.dancerNames ? s.dancerNames : who

  return {
    occurrenceId: s.occurrenceId,
    /*
     * Named on her own row, because three of Ashley Tuesday classes are solos
     * and all three read "Solo" without it — a time being the only thing
     * telling them apart. A dancer own row leaves it off: that row is already
     * headed with her name.
     */
    label: named ? `${label} — ${named}` : label,
    detail,
    from: clock(s.startTime),
    to: clock(s.endTime),
    when: formatSpan(clock(s.startTime), clock(s.endTime)),
    roomName: s.roomName,
    roomColor: s.roomColor,
    taughtBy: [],
    mine: true,
    isCancelled: s.isCancelled,
    note: null,
    // The real one, not a copy of its rule. It carries D-369 — a private has
    // no register to finish, so counting it inflates a number that can never
    // be cleared — and two implementations of that would drift.
    needsMarking: needsMarking(s),
    // The real one, for the same reason as the line above it.
    registerId: registerOf(s),
    // A private has no register at all (D-369), so it has no count either.
    marked: s.isPrivate || s.isCancelled || s.enrolled === 0
      ? null
      : { done: s.marked, of: s.enrolled },
  }
}

/** A dancer's session, as a row class. */
function fromDancing(s: PortalSession): RowClass {
  const { label, detail } = describeClass(s.className, null)

  return {
    occurrenceId: s.occurrenceId,
    label,
    detail,
    from: clock(s.startTime),
    to: clock(s.endTime),
    when: formatSpan(clock(s.startTime), clock(s.endTime)),
    roomName: s.roomName,
    roomColor: s.roomColor,
    taughtBy: s.instructors,
    mine: false,
    isCancelled: s.isCancelled,
    note: null,
    needsMarking: false,
    // A family never takes a register, so her row offers none.
    registerId: null,
    // Whether her class is marked is the studio business, not her family.
    marked: null,
  }
}

const onDay = (date: string, today: string) => date === today

/**
 * Builds every row for one day.
 *
 * <paramref name="teaching"/> is empty for a parent who does not teach, and
 * <paramref name="dancers"/> empty for a teacher with no children here — the
 * shape is the same either way, which is what makes this one screen rather
 * than three.
 */
export function todayRows(
  today: string,
  teaching: TeacherSession[],
  dancers: { name: string; sessions: PortalSession[]; startsOn?: string | null; self?: boolean }[],
  teaches = true,
): TodayRow[] {
  const mine = teaching.filter((s) => onDay(s.date, today))
  const rows: TodayRow[] = []

  const theirs = dancers.map((d) => ({
    name: d.name,
    self: d.self === true,
    sessions: d.sessions.filter((s) => onDay(s.date, today)),
  }))

  /*
   * Which occurrences she teaches, so a class her own child is in can say so
   * once rather than appearing on two rows with nothing linking them. Three of
   * Ashley Alley's Wednesday classes are exactly this.
   */
  const taught = new Set(mine.map((s) => s.occurrenceId))

  if (mine.length > 0) {
    const classes = mine
      .map(fromTeaching)
      .sort((a, b) => a.from - b.from)
      .map((c) => {
        const alsoHers = theirs
          .filter((d) => d.sessions.some((s) => s.occurrenceId === c.occurrenceId))
          // A class she assists and also dances says so in the first person
          // (D-572); "Ava is in this one" on Ava's own row is the same defect.
          .map((d) => (d.self ? 'you' : d.name))

        return {
          ...c,
          note:
            alsoHers.length > 0
              ? `${listed(alsoHers)} ${alsoHers.length === 1 && alsoHers[0] === 'you' ? 'are' : 'is'} in this one`
              : null,
        }
      })

    rows.push(row('teaching', 'You are teaching', 'me', classes, null, true))
  } else if (teaches) {
    // She teaches, just not today. The row says so rather than vanishing.
    rows.push(row('teaching', 'You are teaching', 'me', [], nextAfter(teaching, today), true))
  }

  for (const [i, dancer] of theirs.entries()) {
    /*
     * <b>A dancer reading her own row is not a third person</b> (D-572).
     *
     * The label is a first name because these rows are usually a parent's
     * children. A student teacher with her own login is the reader for whom
     * that is wrong: she would get <i>You are teaching</i> over <i>Ava</i>,
     * the second sounding like somebody else in the family. Same shape, same
     * grammar as the teaching row above it.
     */
    const heading = dancer.self ? 'You are dancing' : dancer.name

    if (dancer.sessions.length === 0) {
      rows.push(row(dancer.name, heading, 'dancer', [],
        nextAfter(dancers[i].sessions, today, dancers[i].startsOn), dancer.self))
      continue
    }

    const classes = dancer.sessions
      .map(fromDancing)
      .sort((a, b) => a.from - b.from)
      .map((c, i, all) => ({
        ...c,
        mine: taught.has(c.occurrenceId),
        note: noteFor(c, all[i - 1], taught, theirs, dancer.name),
      }))

    rows.push(row(dancer.name, heading, 'dancer', classes, null, dancer.self))
  }

  /*
   * **Everybody keeps a row, including a dancer in nothing at all** (D-384).
   *
   * This used to drop any row with no classes today and no next date, which
   * contradicted D-380's own text — *"a child who simply is not dancing has to
   * say so rather than disappear"* — and quietly defeated the reason that rule
   * exists (D-209): an absent row cannot be told apart from one that failed to
   * load. A household with two children and one enrolment showed a single row
   * and no explanation, which reads as the app having lost a child.
   *
   * D-381 made "a dancer who dances nothing" a case the owner can *see* and
   * switch off, so this is a real family shape rather than a hypothetical. The
   * row says so in the sentence the portal already uses for it.
   *
   * The teaching row is the one exception and it is filtered above, not here:
   * `teaches` is false for a parent, so she never gets an empty "You are
   * teaching" heading to be puzzled by.
   */
  return rows
}

/**
 * What is worth saying about one of a dancer's classes.
 *
 * In order of use to somebody standing in the building: you are teaching it,
 * she stays where she is, a sibling is in the same one, or she changes room.
 */
function noteFor(
  c: RowClass,
  previous: RowClass | undefined,
  taught: Set<string>,
  everyone: { name: string; sessions: PortalSession[] }[],
  self: string,
): string | null {
  if (taught.has(c.occurrenceId)) return 'you teach this one'

  const withSibling = everyone
    .filter((d) => d.name !== self && d.sessions.some((s) => s.occurrenceId === c.occurrenceId))
    .map((d) => d.name)

  if (withSibling.length > 0) return `same class as ${listed(withSibling)}`

  if (previous && previous.roomName && c.roomName) {
    return previous.roomName === c.roomName ? 'same room, stays put' : 'moves rooms'
  }

  return null
}

/**
 * The soonest day after today with anything on it, and how much.
 *
 * Null when there is nothing known at all — the schedule window is a fortnight
 * (D-176), so a dancer whose season starts later has no answer here and the row
 * says only that today is empty.
 */
function nextAfter(
  sessions: { date: string; startTime?: string | null }[],
  today: string,
  beyondTheWindow?: string | null,
): { date: string; classes: number; from: string | null } | null {
  const later = sessions.filter((s) => s.date > today)

  if (later.length === 0) {
    /*
     * Nothing in the fortnight either payload carries (D-176), so fall back to
     * the date the server worked out for exactly this - a dancer whose season
     * has not started yet. Without it the row says "nothing today" and stops,
     * which is the empty screen D-189 exists to prevent.
     */
    return beyondTheWindow ? { date: beyondTheWindow, classes: 0, from: null } : null
  }

  const date = later.map((s) => s.date).reduce((a, b) => (b < a ? b : a))
  const thatDay = later.filter((s) => s.date === date)

  const starts = thatDay.map((s) => s.startTime).filter((t): t is string => Boolean(t)).sort()

  return { date, classes: thatDay.length, from: starts[0] ?? null }
}

function row(
  key: string,
  title: string,
  subject: Subject,
  classes: RowClass[],
  next: { date: string; classes: number; from: string | null } | null,
  self = false,
): TodayRow {
  const parts = dayShape(classes.map((c) => ({ startTime: minutes(c.from), endTime: minutes(c.to) })))

  return {
    key,
    title,
    subject,
    self,
    classes,
    parts,
    next,
    sentence: {
      spans: parts.filter((p) => p.kind === 'busy').map((p) => formatSpan(p.from, p.to)),
      gaps: parts
        .filter((p) => p.kind === 'gap')
        .map((p) => `${gapLabel(p.minutes)} ${gapWord(subject)}`),
    },
  }
}

/** Back to the wire format `dayShape` parses, so there is one parser. */
const minutes = (total: number) =>
  `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}:00`

/** "Hailey", "Hailey and Rivers", "Hailey, Rivers and Lainey". */
function listed(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The one sentence neither half can say on its own.
 *
 * Victoria finishes teaching before two of her three children are done, and
 * the third goes on another hour and a quarter. That is a collection problem,
 * and it needs both payloads at once.
 */
export function tonight(rows: TodayRow[]): string | null {
  const everyone = rows
    .filter((r) => r.classes.length > 0)
    .map((r) => ({
      title: r.title,
      // Who is reading decides the pronoun; the kind of row no longer does.
      self: r.self,
      end: Math.max(...r.classes.map((c) => c.to)),
    }))

  /*
   * <b>The reader is one person, however many rows she has</b> (D-572).
   *
   * This sentence compares people, and a student teacher who assists at 4:30
   * and dances at 5:15 has two rows that are both her. Left as two it said
   * <i>"You are done at 5:30pm. You go on to 6:45pm."</i> - correct pronouns
   * and a false statement, in the loudest element on the panel. She is done at
   * 6:45; there is nobody for her to be compared with.
   *
   * Collapsed to the latest of her own ends, which also disposes of
   * <i>"You and you are done at 6:15pm"</i> when both halves finished together.
   * A household where she is the only person then falls out at the guard
   * below, which is right: with one person there is nothing to say.
   */
  const mine = everyone.filter((e) => e.self)
  const ends = mine.length > 1
    ? [{ ...mine.reduce((a, b) => (b.end > a.end ? b : a)) }, ...everyone.filter((e) => !e.self)]
    : everyone

  if (ends.length < 2) return null

  const last = ends.reduce((a, b) => (b.end > a.end ? b : a))
  const rest = ends.filter((e) => e.end < last.end)
  if (rest.length === 0) return null

  /*
   * Only those who finish *first*, not everybody else.
   *
   * Taking the minimum across all of them said "you, Hailey and Rivers are done
   * at 6:30" on an evening where Victoria teaches until 8 — true of two of the
   * three and wrong about the one reading it. The sentence is a collection, so
   * it names the collection that comes first and the one that comes last.
   */
  const earliest = Math.min(...rest.map((e) => e.end))
  const others = rest.filter((e) => e.end === earliest)

  const earlier = listed(others.map((e) => (e.self ? 'you' : e.title)))

  // "You are done" reads wrong; "Hailey is done" reads right. One name that is
  // not the reader takes the singular, everything else takes the plural.
  const verb = others.length === 1 && !others[0].self ? 'is' : 'are'
  const who = last.self ? 'You go on' : `${last.title} goes on`

  return `${capitalised(earlier)} ${verb} done at ${formatTime(earliest)}. ${who} to ${formatTime(last.end)}.`
}

const capitalised = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
